import * as React from 'react';
import uniq from 'lodash/uniq';
import { DatasetComponentOption, SeriesOption } from 'echarts';
import { getFormattedValue } from '@/lowcode/common/comoponents/util';
import { getObjectSetterSnippet } from '@/lowcode/common/manager/setter-snippet';
import { ObjectSetter } from '@/types/setter';
import { AGGREGATION_SETTERS, SORTER_SETTERS } from '../const';
import { uuid } from '../../util/util';
import { RealField } from '../../types';
import { IntervalRequestConfig } from './type';

const { ActionSetter } = window.VisualEngineUtils || {};

export interface RealIntervalFields {
  xField: RealField[];
  yField: RealField[];
  categoryField?: RealField[];
}

export const getIntervalFields = (requestConfig?: IntervalRequestConfig): RealIntervalFields => {
  if (requestConfig?.metaConfig) {
    return {
      xField:
        requestConfig.metaConfig?.xField?.records?.map((record) => {
          return {
            fieldName: record.name,
            ...record.settings,
          };
        }) || [],
      yField:
        requestConfig.metaConfig?.yField?.records?.map((record) => {
          return {
            fieldName: record.name,
            ...record.settings,
          };
        }) || [],
      categoryField: requestConfig.metaConfig?.categoryField?.records?.map((record) => {
        return {
          fieldName: record.name,
          ...record.settings,
        };
      }),
    };
  }

  return {
    xField: requestConfig?.xField || [],
    yField: requestConfig?.yField || [],
  };
};

export const generateFormilyConfig = ({
  showMeasureGroup,
}: {
  /* 是否展示聚合面板 */
  showMeasureGroup?: boolean;
}): ObjectSetter[] => {
  return [
    {
      name: 'settings',
      title: 'settings',
      display: 'plain',
      setter: getObjectSetterSnippet({
        items: [
          {
            name: 'aliasName',
            title: '字段别名',
            display: 'inline',
            setter: {
              componentName: 'MixedSetter',
              props: {
                setters: [
                  {
                    title: '文本',
                    componentName: 'StringSetter',
                  },
                  {
                    title: 'JS脚本',
                    componentName: (
                      <ActionSetter
                        title="JS脚本"
                        supportTypes={['page']}
                        defaultCode={`/**
* @description 通过脚本设置字段别名
* @param fieldName 字段原始名称
* @returns string  字段别名
* */
function getAliasName(fieldName) {
  return '';
}`}
                        defaultActionName={'getAliasName'}
                      />
                    ),
                  },
                ],
              },
            },
          },

          ...(showMeasureGroup ? AGGREGATION_SETTERS : []),
          ...SORTER_SETTERS,
        ],
      }),
    },
  ];
};

export function getIntervalDataSet(
  listData: any[],
  intervalFields: RealIntervalFields,
): {
  dataset: DatasetComponentOption;
  series: SeriesOption[];
  yFieldKeys: string[];
} {
  const { xField, yField, categoryField } = intervalFields;
  const xFieldName = xField?.[0]?.fieldName;
  const xFieldAliasName = getFormattedValue(
    xField?.[0]?.aliasName || xFieldName,
    xFieldName,
  ) as string;

  // 存在分类字段的场景
  if (categoryField?.length) {
    const yFieldName = yField?.[0]?.fieldName;
    const categoryFieldName = categoryField[0]?.fieldName;

    const xFieldValues = uniq(listData.map((item) => item[xFieldName]));
    const categoryFieldValues = uniq(listData.map((item) => item[categoryFieldName]));

    const data = xFieldValues.map((xFieldValue) => {
      const temp = categoryFieldValues.reduce((acc, categoryFieldValue) => {
        // 筛选出匹配当前xField和类别字段值的数据（用户数据集应当保证匹配条件的数据唯一性）
        const target = listData.filter(
          (item) =>
            item[xFieldName] === xFieldValue && item[categoryFieldName] === categoryFieldValue,
        )[0];

        return target
          ? { ...acc, [categoryFieldValue]: target[yFieldName] }
          : { ...acc, [categoryFieldValue]: null };
      }, {});

      return { [xFieldName]: xFieldValue, ...temp };
    });

    return {
      yFieldKeys: categoryFieldValues,
      dataset: {
        dimensions: [
          { name: xFieldName, displayName: xFieldAliasName },
          ...categoryFieldValues.map((cate) => {
            return { name: `${cate}`, displayName: `${cate}` };
          }),
        ],
        source: data,
      },
      series: categoryFieldValues?.map((cate) => {
        return {
          name: `${cate}`,
          encode: {
            x: xFieldName,
            y: `${cate}`,
          },
        };
      }),
    };
  }

  return {
    yFieldKeys: yField.map((y) => y.fieldName),
    dataset: {
      dimensions: [
        { name: xFieldName, displayName: xFieldAliasName },
        ...(yField?.map((y) => {
          return {
            name: y.fieldName,
            displayName: getFormattedValue(y.aliasName || y.fieldName, y.fieldName) as string,
          };
        }) || []),
      ],
      source: listData,
    },
    series: yField?.map((y) => {
      return {
        name: getFormattedValue(y.aliasName || y.fieldName, y.fieldName) as string,
        encode: {
          x: xFieldName,
          y: y.fieldName,
        },
      };
    }),
  };
}

// 为堆叠图添加空堆叠，用于显示最顶部合计值
export function addEmptyStack(baseDataSet: {
  dataset: DatasetComponentOption;
  series: SeriesOption[];
}): {
  dataset: DatasetComponentOption;
  series: SeriesOption[];
} {
  const { dataset, series } = baseDataSet;
  const emptyStackName = uuid(8);

  if (!Array.isArray(dataset?.source)) {
    return {
      dataset,
      series,
    };
  }

  const datasetWithEmpty = {
    ...dataset,
    dimensions: dataset.dimensions?.concat({
      name: emptyStackName,
    }),
    source: dataset.source.map((item) => {
      return { ...item, [emptyStackName]: 0 };
    }),
  };

  const seriesWithEmpty = series.concat({
    tooltip: {
      show: false,
    },
    itemStyle: {
      color: 'none',
    },
    encode: {
      x: 'month',
      y: emptyStackName,
    },
  });

  return {
    dataset: datasetWithEmpty,
    series: seriesWithEmpty,
  };
}
